/**
 * MediaCard.jsx — a single YouTube-style media tile for RotoTube.
 *
 * Renders a normalized view-model (VM): 12px-rounded 16:9 thumbnail with a
 * duration badge, a 2-line title, and a gray metadata line. On hover it brightens
 * and reveals a ⋮ kebab (only for real media items — folders/shoots navigate and
 * have no actions). Collections render as YouTube "playlist" tiles.
 */

import { Box, Typography, IconButton, Chip } from '@mui/material';
import {
  MoreVert as KebabIcon, PlayArrow as PlayArrowIcon, Folder as FolderIcon,
  PhotoLibrary as PhotoIcon, Favorite as HeartIcon, PlaylistPlay as PlaylistIcon,
} from '@mui/icons-material';
import { YT, fmtDuration } from './ytTheme';

function Placeholder({ icon }) {
  return (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(255,255,255,0.22)' }}>
      {icon === 'folder' ? <FolderIcon sx={{ fontSize: 46 }} /> : <PhotoIcon sx={{ fontSize: 40 }} />}
    </Box>
  );
}

export default function MediaCard({ vm, favorited, onPlay, onMenu }) {
  const {
    thumb, title, subtitle, duration, badge, disabled, dim,
    play, descriptor, onOpen, icon = 'play', playlist,
  } = vm;

  const handleClick = () => {
    if (disabled) return;
    if (play) return onPlay?.(vm);
    if (onOpen) return onOpen();
  };

  const badgeText = badge || (duration ? fmtDuration(duration) : '');

  return (
    <Box
      className="rt-card"
      onClick={handleClick}
      sx={{
        cursor: disabled ? 'default' : 'pointer', display: 'flex', flexDirection: 'column', gap: 1,
        opacity: disabled ? 0.55 : 1,
      }}
    >
      {/* Thumbnail */}
      <Box sx={{
        position: 'relative', width: '100%', aspectRatio: '16 / 9', borderRadius: '12px',
        overflow: 'hidden', bgcolor: '#181818', border: `1px solid ${YT.border}`,
      }}>
        {thumb ? (
          <Box
            component="img" src={thumb} loading="lazy" alt=""
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
            sx={{ width: '100%', height: '100%', objectFit: 'cover', opacity: dim ? 0.5 : 1, transition: 'filter 150ms', '.rt-card:hover &': { filter: disabled ? 'none' : 'brightness(1.06)' } }}
          />
        ) : <Placeholder icon={icon} />}

        {/* Center play affordance on hover (video/image only) */}
        {icon === 'play' && play && (
          <Box sx={{
            position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
            opacity: 0, transition: 'opacity 150ms', bgcolor: 'rgba(0,0,0,0.28)',
            '.rt-card:hover &': { opacity: 1 },
          }}>
            <Box sx={{ width: 46, height: 46, borderRadius: '50%', bgcolor: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <PlayArrowIcon sx={{ fontSize: 30, color: '#fff' }} />
            </Box>
          </Box>
        )}

        {/* Playlist (collection) right-edge panel — YouTube style */}
        {playlist && (
          <Box sx={{
            position: 'absolute', top: 0, right: 0, bottom: 0, width: '38%',
            bgcolor: 'rgba(0,0,0,0.72)', display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center', gap: 0.5,
          }}>
            <PlaylistIcon sx={{ color: '#fff', fontSize: 22 }} />
            <Typography sx={{ color: '#fff', fontSize: '0.9rem', fontWeight: 600, lineHeight: 1 }}>{playlist.count}</Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              {playlist.count === 1 ? 'video' : 'videos'}
            </Typography>
          </Box>
        )}

        {/* Duration / kind badge */}
        {badgeText && !playlist && (
          <Chip label={badgeText} size="small" sx={{
            position: 'absolute', bottom: 8, right: 8, height: 20, bgcolor: 'rgba(0,0,0,0.82)',
            color: '#fff', fontSize: '0.7rem', fontWeight: 500, borderRadius: '4px',
            '& .MuiChip-label': { px: 0.75 },
          }} />
        )}

        {/* Favorited heart (top-left) */}
        {favorited && (
          <Box sx={{ position: 'absolute', top: 8, left: 8, width: 24, height: 24, borderRadius: '50%', bgcolor: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <HeartIcon sx={{ fontSize: 15, color: YT.accent }} />
          </Box>
        )}

        {/* Kebab (actions) — only for real, playable media items, on hover */}
        {descriptor && !disabled && (
          <IconButton
            size="small"
            onClick={(e) => { e.stopPropagation(); onMenu?.(e, vm); }}
            sx={{
              position: 'absolute', top: 4, right: 4, color: '#fff', bgcolor: 'rgba(0,0,0,0.5)',
              opacity: 0, transition: 'opacity 120ms', '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' },
              '.rt-card:hover &': { opacity: 1 }, '&:focus-visible': { opacity: 1 },
              '@media (hover: none)': { opacity: 1 },
            }}
          >
            <KebabIcon sx={{ fontSize: 18 }} />
          </IconButton>
        )}
      </Box>

      {/* Title + metadata */}
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{
          fontSize: '0.9rem', fontWeight: 500, color: YT.text, lineHeight: 1.3,
          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
        }}>{title}</Typography>
        {subtitle && (
          <Typography sx={{ fontSize: '0.78rem', color: YT.textSecondary, mt: 0.25 }} noWrap>{subtitle}</Typography>
        )}
      </Box>
    </Box>
  );
}
